// src/pages/OAuth2RedirectPage.jsx

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { saveToken, notifyLogin, isUUID, saveUserInfo } from "../Utils/auth";
import { path_url } from "../Config/config";

const OAuth2RedirectPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading | error
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    // Read the URL parameters
    // Spring Boot sends either:
    // /oauth2/redirect?token=<jwt>   ← success
    // /oauth2/redirect?error=<msg>   ← failure
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const error = params.get("error");

    if (error) {
      // Google login failed
      console.error("OAuth2 login failed:", error);
      setErrorMessage(decodeURIComponent(error));
      setStatus("error");

      // Redirect back to login after 3 seconds
      setTimeout(() => {
        navigate("/login");
      }, 3000);
      return;
    }

    if (token) {
      // Google login succeeded — save token
      saveToken(token);

      // Fetch user info from GET /user to populate name, userId, adminId, status
      // This mirrors what Login.jsx does after normal login
      const fetchUserAndRedirect = async () => {
        try {
          const response = await fetch(`${path_url}/user`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });

          if (response.ok) {
            const data = await response.json();
            console.log("OAuth2: /user response:", data);

            // Save to localStorage exactly as Login.jsx does
            if (data.id) {
              localStorage.setItem("userId", data.id);
            }
            if (data.adminid) {
              localStorage.setItem("adminId", data.adminid);
            } else if (data.id && isUUID(data.id)) {
              localStorage.setItem("adminId", data.id);
            }
            if (data.name) {
              const displayName = data.name.includes("@") ? data.name.split("@")[0] : data.name;
              localStorage.setItem("name", displayName);
            } else if (data.email) {
              localStorage.setItem("name", data.email.split("@")[0]);
            }
            if (data.status) {
              localStorage.setItem("status", data.status);
            }

            const rawId = isUUID(data.id) ? data.id : null;
            const rawAdminId = isUUID(data.adminid) ? data.adminid : rawId;

            if (rawId) {
              saveUserInfo({
                id: rawId,
                useradminid: rawAdminId,
                name: data.name || (data.email ? data.email.split("@")[0] : "User"),
                email: data.email || "",
                status: data.status || "Active",
                usertype: data.usertype || "GOOGLE",
              });
            }

            console.log("Google OAuth2 login: user info saved successfully.", {
              userId: localStorage.getItem("userId"),
              adminId: localStorage.getItem("adminId"),
              name: localStorage.getItem("name"),
            });

            // Notify login so UserContext updates
            notifyLogin();

            // Navigate to dashboard only after complete initialization
            navigate("/crm-dashboard", { replace: true });
          } else {
            console.error("OAuth2: GET /user returned status", response.status);
            setStatus("error");
            setErrorMessage("Failed to load user profile. Redirecting to login...");
            setTimeout(() => {
              navigate("/login");
            }, 3000);
          }
        } catch (e) {
          console.error("OAuth2: Failed to fetch /user:", e);
          setStatus("error");
          setErrorMessage("Failed to initialize user session. Redirecting to login...");
          setTimeout(() => {
            navigate("/login");
          }, 3000);
        }
      };

      fetchUserAndRedirect();
      return;
    }

    // Neither token nor error — something went wrong
    console.error("OAuth2 redirect called with no token or error");
    setStatus("error");
    setErrorMessage("Login failed. Please try again.");
    setTimeout(() => {
      navigate("/login");
    }, 3000);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Loading screen while processing
  if (status === "loading") {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.spinner}></div>
          <h2 style={styles.title}>Signing you in...</h2>
          <p style={styles.sub}>Please wait while we complete your Google login.</p>
        </div>
      </div>
    );
  }

  // Error screen
  if (status === "error") {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>❌</div>
          <h2 style={styles.title}>Login Failed</h2>
          <p style={styles.sub}>{errorMessage || "Something went wrong. Redirecting you back..."}</p>
        </div>
      </div>
    );
  }

  return null;
};

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f9f9f7"
  },
  card: {
    background: "#fff",
    borderRadius: 16,
    padding: "48px 40px",
    textAlign: "center",
    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
    maxWidth: 380,
    width: "100%"
  },
  spinner: {
    width: 44,
    height: 44,
    border: "3px solid #f0f0f0",
    borderTop: "3px solid #1A56DB",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    margin: "0 auto 20px"
  },
  errorIcon: {
    fontSize: 44,
    marginBottom: 16
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
    color: "#111827",
    marginBottom: 8
  },
  sub: {
    fontSize: 14,
    color: "#6B7280",
    lineHeight: 1.6
  }
};

export default OAuth2RedirectPage;
